import React, { useState } from 'react';

/**
 * SovereigntyConsent — Dual-Consent Modal for Data Sovereignty
 *
 * This is a HARD STOP gate in the onboarding flow. If either consent
 * checkbox is unchecked, GenAI features remain locked for this client.
 *
 * Consent A (Processing):
 *   "I authorize the system to distill teacher-level schema definitions
 *    into a student-sql-intelligence model for natural language querying."
 *
 * Consent B (Data Perimeter / In-and-Out Guarantee):
 *   "I understand and approve that all distilled intelligence is strictly
 *    confined to the local organization environment. No metadata, schema
 *    definitions, or intelligence fragments shall exit the organizational
 *    perimeter (In-and-Out) without explicit administrative approval."
 *
 * Architecture:
 *   - Teacher: Full raw QRM database schema (all tables/columns)
 *   - Student: Pruned intelligence map (relational logic only, no PII)
 *   - Distillation happens entirely within localhost:3000 / Docker
 *   - No external API calls, no training on customer data
 */
const SovereigntyConsent = ({
  schemaCrawlResult,
  onConsentGranted,
  onConsentDenied,
  clientName,
  loading,
}) => {
  const [consentA, setConsentA] = useState(false); // Processing consent
  const [consentB, setConsentB] = useState(false); // Data perimeter consent
  const [showDetails, setShowDetails] = useState(false);

  const bothGranted = consentA && consentB;
  const tableCount = schemaCrawlResult?.summary?.table_count || 0;
  const columnCount = schemaCrawlResult?.summary?.total_columns || 0;
  const objectCount = schemaCrawlResult?.summary?.object_count || 0;

  return (
    <div className="sovereignty-consent-modal">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        color: '#fff', padding: '24px 28px', borderRadius: '12px 12px 0 0',
        display: 'flex', alignItems: 'center', gap: '12px',
      }}>
        <span style={{ fontSize: '32px' }}>🛡️</span>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>
            Data Sovereignty & Schema Intelligence Consent
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.85 }}>
            {clientName || 'Client'} — Secure Schema-Intelligence Bridge
          </p>
        </div>
      </div>

      <div style={{ padding: '24px 28px' }}>
        {/* Data Perimeter Guarantee Banner */}
        <div style={{
          background: '#fff3e0', border: '1px solid #ff9800', borderRadius: '8px',
          padding: '14px 16px', marginBottom: '20px', display: 'flex', gap: '10px',
        }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <div style={{ fontSize: '13px', lineHeight: 1.5 }}>
            <strong>Data Perimeter Guarantee Required</strong><br />
            This is a mandatory legal and technical consent gate. GenAI features
            will remain <strong>locked</strong> until both consent requirements are acknowledged.
            This ensures compliance with CISO data leakage prevention policies.
          </div>
        </div>

        {/* Schema Summary */}
        {schemaCrawlResult?.status === 'success' && (
          <div style={{
            background: '#f5f5f5', borderRadius: '8px', padding: '14px 16px',
            marginBottom: '20px', fontSize: '13px',
          }}>
            <strong>Teacher Schema (Raw Input):</strong>
            <div style={{ display: 'flex', gap: '24px', marginTop: '8px' }}>
              <span>📊 {tableCount} Tables</span>
              <span>📋 {columnCount} Columns</span>
              <span>🔧 {objectCount} Objects</span>
            </div>
            <button
              type="button"
              onClick={() => setShowDetails(!showDetails)}
              style={{
                background: 'none', border: 'none', color: '#1565c0',
                cursor: 'pointer', fontSize: '12px', marginTop: '8px', padding: 0,
              }}
            >
              {showDetails ? '▼ Hide table details' : '▶ Show table details'}
            </button>
            {showDetails && schemaCrawlResult.tables && (
              <div style={{
                maxHeight: '150px', overflow: 'auto', marginTop: '8px',
                background: '#fff', borderRadius: '6px', padding: '8px', fontSize: '12px',
              }}>
                {schemaCrawlResult.tables.map(t => (
                  <div key={t.table} style={{ marginBottom: '4px' }}>
                    <code style={{ color: '#1565c0' }}>{t.table}</code>
                    <span style={{ color: '#888', marginLeft: '6px' }}>
                      ({t.columns.slice(0, 6).map(c => c.name).join(', ')}{t.columns.length > 6 ? ', …' : ''})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Distillation Flow Diagram */}
        <div style={{
          background: '#e8eaf6', borderRadius: '8px', padding: '14px 16px',
          marginBottom: '20px', fontSize: '13px', textAlign: 'center',
        }}>
          <strong>Teacher → Student Distillation (Air-Gapped, Local Only)</strong>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
            <span style={{ background: '#c5cae9', padding: '6px 12px', borderRadius: '6px', fontSize: '12px' }}>
              🗄️ Raw Schema<br /><small>(Teacher)</small>
            </span>
            <span style={{ fontSize: '18px' }}>→</span>
            <span style={{ background: '#fff9c4', padding: '6px 12px', borderRadius: '6px', fontSize: '12px' }}>
              🔬 PII Scrub<br /><small>(localhost)</small>
            </span>
            <span style={{ fontSize: '18px' }}>→</span>
            <span style={{ background: '#c8e6c9', padding: '6px 12px', borderRadius: '6px', fontSize: '12px' }}>
              🧠 Student Model<br /><small>(Pruned SQL Intelligence)</small>
            </span>
          </div>
          <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#555' }}>
            All processing occurs within your local environment (localhost:3000 / Docker).
            No external API calls. No model training on your data.
          </p>
        </div>

        {/* Consent A: Processing */}
        <div style={{
          border: `2px solid ${consentA ? '#4caf50' : '#e0e0e0'}`,
          borderRadius: '10px', padding: '16px', marginBottom: '12px',
          background: consentA ? '#f1f8e9' : '#fff', transition: 'all 0.2s',
        }}>
          <label style={{ display: 'flex', gap: '12px', cursor: 'pointer', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={consentA}
              onChange={(e) => setConsentA(e.target.checked)}
              style={{ marginTop: '3px', width: '18px', height: '18px', accentColor: '#4caf50' }}
              aria-label="Consent A: Authorize schema distillation processing"
            />
            <div>
              <strong style={{ fontSize: '14px' }}>Consent A — Distillation Processing Authorization</strong>
              <p style={{ margin: '6px 0 0', fontSize: '13px', lineHeight: 1.5, color: '#333' }}>
                I authorize the system to distill teacher-level schema definitions into a
                student-sql-intelligence model for natural language querying. The distillation
                process will extract relational logic (how Assets relate to Liabilities, how
                Deposits relate to Products) while removing all sensitive data markers and PII.
              </p>
            </div>
          </label>
        </div>

        {/* Consent B: Data Perimeter */}
        <div style={{
          border: `2px solid ${consentB ? '#4caf50' : '#e0e0e0'}`,
          borderRadius: '10px', padding: '16px', marginBottom: '20px',
          background: consentB ? '#f1f8e9' : '#fff', transition: 'all 0.2s',
        }}>
          <label style={{ display: 'flex', gap: '12px', cursor: 'pointer', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={consentB}
              onChange={(e) => setConsentB(e.target.checked)}
              style={{ marginTop: '3px', width: '18px', height: '18px', accentColor: '#4caf50' }}
              aria-label="Consent B: Data perimeter guarantee"
            />
            <div>
              <strong style={{ fontSize: '14px' }}>Consent B — Data Perimeter Guarantee (In-and-Out)</strong>
              <p style={{ margin: '6px 0 0', fontSize: '13px', lineHeight: 1.5, color: '#333' }}>
                I understand and approve that all distilled intelligence is strictly confined
                to the local organization environment. No metadata, schema definitions, or
                intelligence fragments shall exit the organizational perimeter (In-and-Out)
                without explicit administrative approval. The LLM inference occurs via a local
                or encrypted private endpoint that does not train on organizational data.
              </p>
            </div>
          </label>
        </div>

        {/* Hard Stop Warning */}
        {!bothGranted && (
          <div style={{
            background: '#ffebee', border: '1px solid #ef5350', borderRadius: '8px',
            padding: '12px 16px', marginBottom: '16px', fontSize: '13px',
            display: 'flex', gap: '8px', alignItems: 'center',
          }}>
            <span style={{ fontSize: '18px' }}>🔒</span>
            <span>
              <strong>Hard Stop:</strong> Both consent checkboxes must be acknowledged to enable
              GenAI schema intelligence features. Without consent, the system operates in
              document-only mode (PDF-based GenAI without database intelligence).
            </span>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onConsentDenied}
            style={{
              padding: '10px 20px', background: '#f5f5f5', border: '1px solid #ddd',
              borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
            }}
          >
            Skip — Proceed Without Schema Intelligence
          </button>
          <button
            type="button"
            onClick={() => onConsentGranted({ consentA, consentB })}
            disabled={!bothGranted || loading}
            style={{
              padding: '10px 24px',
              background: bothGranted ? '#2e7d32' : '#bdbdbd',
              color: '#fff', border: 'none', borderRadius: '8px',
              cursor: bothGranted ? 'pointer' : 'not-allowed',
              fontSize: '13px', fontWeight: 700,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? '🔄 Processing...' : '✅ Grant Consent & Distill Intelligence'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SovereigntyConsent;
